import React, { useState } from 'react';

import Button from '../../../components/ui/Button';

const TradingHeatmap = ({ dateRange, selectedAccounts }) => {
  const [viewType, setViewType] = useState('profit'); // 'profit', 'volume', 'trades'
  const [selectedWeek, setSelectedWeek] = useState(null);

  // Mock trading activity data by hour and day
  const generateHeatmapData = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    const hours = Array.from({ length: 24 }, (_, i) => i);
    const data = [];

    days?.forEach((day, dayIndex) => {
      hours?.forEach(hour => {
        // Simulate trading activity patterns
        let baseActivity = 0;
        
        // Higher activity during market hours
        if (hour >= 8 && hour <= 17) {
          baseActivity = Math.random() * 100 + 50;
        } else if (hour >= 18 && hour <= 22) {
          baseActivity = Math.random() * 60 + 20;
        } else {
          baseActivity = Math.random() * 20;
        }

        // Reduce activity on Friday afternoons
        if (dayIndex === 4 && hour >= 15) {
          baseActivity *= 0.6;
        }

        // Add some randomness
        const variance = (Math.random() - 0.5) * 40;
        const activity = Math.max(0, baseActivity + variance);

        data?.push({
          day,
          dayIndex,
          hour,
          profit: activity * (Math.random() > 0.3 ? 1 : -1) * 10,
          volume: activity * 1000,
          trades: Math.floor(activity / 10),
          intensity: activity
        });
      });
    });

    return data;
  };

  const heatmapData = generateHeatmapData();

  const getIntensityColor = (value, maxValue, type) => {
    const intensity = Math.abs(value) / maxValue;
    
    if (type === 'profit') {
      if (value > 0) {
        return `rgba(5, 150, 105, ${intensity * 0.8})`;
      } else {
        return `rgba(220, 38, 38, ${intensity * 0.8})`;
      }
    } else {
      return `rgba(30, 64, 175, ${intensity * 0.8})`;
    }
  };

  const maxValues = {
    profit: Math.max(...heatmapData?.map(d => Math.abs(d?.profit))),
    volume: Math.max(...heatmapData?.map(d => d?.volume)),
    trades: Math.max(...heatmapData?.map(d => d?.trades))
  };

  const formatValue = (value, type) => {
    switch (type) {
      case 'profit':
        return new Intl.NumberFormat('en-US', {
          style: 'currency',
          currency: 'USD',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        })?.format(value);
      case 'volume':
        return new Intl.NumberFormat('en-US', {
          notation: 'compact',
          maximumFractionDigits: 1
        })?.format(value);
      case 'trades':
        return value?.toString();
      default:
        return value?.toString();
    }
  };

  const getHourLabel = (hour) => {
    if (hour === 0) return '12 AM';
    if (hour === 12) return '12 PM';
    if (hour < 12) return `${hour} AM`;
    return `${hour - 12} PM`;
  };

  const handleCellClick = (data) => {
    console.log('Cell clicked:', data);
    // In a real app, this would show detailed breakdown
  };

  const getTotalsByDay = () => {
    const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
    return days?.map(day => {
      const dayData = heatmapData?.filter(d => d?.day === day);
      return {
        day,
        profit: dayData?.reduce((sum, d) => sum + d?.profit, 0),
        volume: dayData?.reduce((sum, d) => sum + d?.volume, 0),
        trades: dayData?.reduce((sum, d) => sum + d?.trades, 0)
      };
    });
  };

  const getTotalsByHour = () => {
    const hours = Array.from({ length: 24 }, (_, i) => i);
    return hours?.map(hour => {
      const hourData = heatmapData?.filter(d => d?.hour === hour);
      return {
        hour,
        profit: hourData?.reduce((sum, d) => sum + d?.profit, 0),
        volume: hourData?.reduce((sum, d) => sum + d?.volume, 0),
        trades: hourData?.reduce((sum, d) => sum + d?.trades, 0)
      };
    });
  };

  const dayTotals = getTotalsByDay();
  const hourTotals = getTotalsByHour();

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">
            Trading Activity Heatmap
          </h3>
          <p className="text-sm text-muted-foreground">
            Activity patterns by hour and day of the week
          </p>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mt-4 lg:mt-0">
          <div className="flex bg-muted rounded-md p-1">
            <Button
              variant={viewType === 'profit' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewType('profit')}
              className="text-xs"
            >
              P&L
            </Button>
            <Button
              variant={viewType === 'volume' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewType('volume')}
              className="text-xs"
            >
              Volume
            </Button>
            <Button
              variant={viewType === 'trades' ? 'default' : 'ghost'}
              size="sm"
              onClick={() => setViewType('trades')}
              className="text-xs"
            >
              Trades
            </Button>
          </div>
        </div>
      </div>
      <div className="overflow-x-auto">
        <div className="min-w-full">
          {/* Hour labels */}
          <div className="flex mb-2">
            <div className="w-20 flex-shrink-0"></div>
            <div className="flex-1 grid grid-cols-24 gap-1">
              {Array.from({ length: 24 }, (_, hour) => (
                <div
                  key={hour}
                  className="text-xs text-muted-foreground text-center py-1"
                  style={{ minWidth: '32px' }}
                >
                  {hour % 4 === 0 ? getHourLabel(hour) : ''}
                </div>
              ))}
            </div>
            <div className="w-20 flex-shrink-0 text-center text-xs text-muted-foreground py-1">
              Daily Total
            </div>
          </div>

          {/* Heatmap grid */}
          {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday']?.map((day, dayIndex) => (
            <div key={day} className="flex mb-1">
              <div className="w-20 flex-shrink-0 flex items-center">
                <span className="text-sm font-medium text-foreground">{day}</span>
              </div>
              <div className="flex-1 grid grid-cols-24 gap-1">
                {Array.from({ length: 24 }, (_, hour) => {
                  const cellData = heatmapData?.find(d => d?.day === day && d?.hour === hour);
                  const value = cellData ? cellData?.[viewType] : 0;
                  const backgroundColor = getIntensityColor(value, maxValues?.[viewType], viewType);
                  
                  return (
                    <div
                      key={hour}
                      className="aspect-square rounded cursor-pointer hover:ring-2 hover:ring-primary transition-all duration-200 flex items-center justify-center group relative"
                      style={{ 
                        backgroundColor,
                        minWidth: '32px',
                        minHeight: '32px'
                      }}
                      onClick={() => handleCellClick(cellData)}
                    >
                      {/* Tooltip */}
                      <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-2 py-1 bg-popover text-popover-foreground text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 pointer-events-none whitespace-nowrap z-10">
                        <div>{day} {getHourLabel(hour)}</div>
                        <div>{formatValue(value, viewType)}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="w-20 flex-shrink-0 flex items-center justify-center">
                <span className="text-xs font-medium text-foreground">
                  {formatValue(dayTotals?.[dayIndex]?.[viewType], viewType)}
                </span>
              </div>
            </div>
          ))}

          {/* Hour totals */}
          <div className="flex mt-4 pt-4 border-t border-border">
            <div className="w-20 flex-shrink-0 flex items-center">
              <span className="text-sm font-medium text-muted-foreground">Hourly Total</span>
            </div>
            <div className="flex-1 grid grid-cols-24 gap-1">
              {hourTotals?.map((hourData, index) => (
                <div
                  key={index}
                  className="text-xs text-center py-1 text-foreground font-medium"
                  style={{ minWidth: '32px' }}
                >
                  {index % 4 === 0 ? formatValue(hourData?.[viewType], viewType) : ''}
                </div>
              ))}
            </div>
            <div className="w-20 flex-shrink-0"></div>
          </div>
        </div>
      </div>
      {/* Legend */}
      <div className="mt-6 pt-6 border-t border-border">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center space-x-4 mb-4 sm:mb-0">
            <span className="text-sm text-muted-foreground">Intensity:</span>
            <div className="flex items-center space-x-2">
              <span className="text-xs text-muted-foreground">Low</span>
              <div className="flex space-x-1">
                {[0.2, 0.4, 0.6, 0.8]?.map((intensity, index) => (
                  <div
                    key={index}
                    className="w-4 h-4 rounded"
                    style={{
                      backgroundColor: viewType === 'profit' 
                        ? `rgba(5, 150, 105, ${intensity})`
                        : `rgba(30, 64, 175, ${intensity})`
                    }}
                  />
                ))}
              </div>
              <span className="text-xs text-muted-foreground">High</span>
            </div>
          </div>

          <div className="flex items-center space-x-4 text-sm">
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-green-500 rounded"></div>
              <span className="text-muted-foreground">Profitable</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-3 h-3 bg-red-500 rounded"></div>
              <span className="text-muted-foreground">Loss</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TradingHeatmap;